import { LinkField } from '@sitecore-jss/sitecore-jss-react';
import { Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import Button from 'components/helpers/Button';
import {
  checkoutAddress,
  setOrderAddress,
  useCheckout,
} from 'components/helpers/Cart/CheckoutHelper';
import { useEffect, useRef, useState } from 'react';
import { classnames } from 'tailwindcss-classnames';
import useDictionary from 'lib/sitecore/use-dictionary';
import InputError from 'components/helpers/SvgIcon/icons/input-error';
import { getSalesforceFlowData, SalesForceData } from 'lib/sitecore/sitecore-cdp';

type ShippingAddressProps = {
  updateSteps: (value: number) => void;
  continueToShippingCTA: LinkField;
  shippingAddressLabel: Field<string>;
  shippingMethodLabel: Field<string>;
  reviewLabel: Field<string>;
};

type ShippingAddressFieldsProps = {
  updateSteps: (value: number) => void;
  continueToShippingCTA: LinkField;
};

const inputCss = classnames('mt-2', 'w-full');
const inputSpacingCss = classnames('pb-6', 'max-w-full', 'md:max-w-[395px]');
const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const zipRegex = /^\d{5}$/;

const validateForm = (
  formData: checkoutAddress,
  changedFields: string[],
  setErrors: (errors: Partial<checkoutAddress>) => void
): boolean => {
  const checkRequiredField = (field: string): string | undefined =>
    field.trim() === `` ? `true` : undefined;
  const newErrors: Partial<checkoutAddress> = {};
  changedFields.forEach((fieldName) => {
    switch (fieldName) {
      case 'firstName':
        newErrors.firstName = checkRequiredField(formData.firstName);
        break;
      case 'lastName':
        newErrors.lastName = checkRequiredField(formData.lastName);
        break;
      case 'address':
        newErrors.address = checkRequiredField(formData.address);
        break;
      case 'city':
        newErrors.city = checkRequiredField(formData.city);
        break;
      case 'state':
        newErrors.state = checkRequiredField(formData.state);
        break;
      case 'phone':
        newErrors.phone = checkRequiredField(formData.phone);
        break;
      case 'email':
        if (!emailRegex.test(formData.email.trim())) {
          newErrors.email = 'true';
        }
        break;
      case 'zipCode':
        if (!zipRegex.test(formData.zipCode.trim()) && fieldName === 'zipCode') {
          newErrors.zipCode = 'true';
        }
        break;
      default:
        break;
    }
  });
  setErrors(newErrors);

  return Object.keys(newErrors).length === 0;
};
const checkValidForm = (formData: checkoutAddress): boolean => {
  if (
    formData.firstName.trim() === '' ||
    formData.lastName.trim() === '' ||
    formData.address.trim() === '' ||
    formData.city.trim() === '' ||
    formData.state.trim() === '' ||
    formData.phone.trim() === '' ||
    !emailRegex.test(formData.email.trim()) ||
    !zipRegex.test(formData.zipCode.trim())
  ) {
    return false;
  }

  return true;
};

const usStates = [
  { name: 'Alabama', abbreviation: 'AL' },
  { name: 'Alaska', abbreviation: 'AK' },
  { name: 'Arizona', abbreviation: 'AZ' },
  { name: 'Arkansas', abbreviation: 'AR' },
  { name: 'California', abbreviation: 'CA' },
  { name: 'Colorado', abbreviation: 'CO' },
  { name: 'Connecticut', abbreviation: 'CT' },
  { name: 'Delaware', abbreviation: 'DE' },
  { name: 'District of Columbia', abbreviation: 'DC' },
  { name: 'Florida', abbreviation: 'FL' },
  { name: 'Georgia', abbreviation: 'GA' },
  { name: 'Hawaii', abbreviation: 'HI' },
  { name: 'Idaho', abbreviation: 'ID' },
  { name: 'Illinois', abbreviation: 'IL' },
  { name: 'Indiana', abbreviation: 'IN' },
  { name: 'Iowa', abbreviation: 'IA' },
  { name: 'Kansas', abbreviation: 'KS' },
  { name: 'Kentucky', abbreviation: 'KY' },
  { name: 'Louisiana', abbreviation: 'LA' },
  { name: 'Maine', abbreviation: 'ME' },
  { name: 'Maryland', abbreviation: 'MD' },
  { name: 'Massachusetts', abbreviation: 'MA' },
  { name: 'Michigan', abbreviation: 'MI' },
  { name: 'Minnesota', abbreviation: 'MN' },
  { name: 'Mississippi', abbreviation: 'MS' },
  { name: 'Missouri', abbreviation: 'MO' },
  { name: 'Montana', abbreviation: 'MT' },
  { name: 'Nebraska', abbreviation: 'NE' },
  { name: 'Nevada', abbreviation: 'NV' },
  { name: 'New Hampshire', abbreviation: 'NH' },
  { name: 'New Jersey', abbreviation: 'NJ' },
  { name: 'New Mexico', abbreviation: 'NM' },
  { name: 'New York', abbreviation: 'NY' },
  { name: 'North Carolina', abbreviation: 'NC' },
  { name: 'North Dakota', abbreviation: 'ND' },
  { name: 'Ohio', abbreviation: 'OH' },
  { name: 'Oklahoma', abbreviation: 'OK' },
  { name: 'Oregon', abbreviation: 'OR' },
  { name: 'Pennsylvania', abbreviation: 'PA' },
  { name: 'Rhode Island', abbreviation: 'RI' },
  { name: 'South Carolina', abbreviation: 'SC' },
  { name: 'South Dakota', abbreviation: 'SD' },
  { name: 'Tennessee', abbreviation: 'TN' },
  { name: 'Texas', abbreviation: 'TX' },
  { name: 'Utah', abbreviation: 'UT' },
  { name: 'Vermont', abbreviation: 'VT' },
  { name: 'Virginia', abbreviation: 'VA' },
  { name: 'Washington', abbreviation: 'WA' },
  { name: 'West Virginia', abbreviation: 'WV' },
  { name: 'Wisconsin', abbreviation: 'WI' },
  { name: 'Wyoming', abbreviation: 'WY' },
];

type ErrorWarningProps = {
  errorMessage: string;
};
const ErrorWarning = ({ errorMessage }: ErrorWarningProps) => {
  return (
    <>
      <div className={classnames('flex', 'pt-2')}>
        <InputError />
        <span className="pl-2 text-sm text-red-destructive-shade">{errorMessage}</span>
      </div>
    </>
  );
};

const buildFormDataFromProfile = (res: SalesForceData): checkoutAddress => {
  return {
    firstName: res.profileData.guestFName ? res.profileData.guestFName : '',
    lastName: res.profileData.guestLName ? res.profileData.guestLName : '',
    company: res.profileData.guestCompany ? res.profileData.guestCompany : '',
    address: res.profileData.address ? res.profileData.address : '',
    city: res.profileData.guestCity ? res.profileData.guestCity : '',
    state: res.profileData.guestState ? res.profileData.guestState : '',
    zipCode: res.profileData.guestZip ? res.profileData.guestZip : '',
    phone: res.profileData.guestPhone ? res.profileData.guestPhone : '',
    email: res.profileData.guestEmail ? res.profileData.guestEmail : '',
    address2: '',
  };
};

const ShippingAddressFields = ({
  continueToShippingCTA,
  updateSteps,
}: ShippingAddressFieldsProps): JSX.Element => {
  const dictionary = useDictionary();
  const { checkoutData } = useCheckout();
  const firstRender = useRef(true);
  const prevAddressData = checkoutData.checkOutData.addressData;
  const [formData, setFormData] = useState<checkoutAddress>(prevAddressData);
  const [boxeverLoaded, setboxeverLoaded] = useState<boolean>(false);
  const [showAddress2, setShowAddress] = useState<boolean>(false);
  const [formIsValid, setFormValid] = useState<boolean>(false);
  const [changedFields, setChangedFields] = useState<string[]>([]);
  useEffect(() => {
    const delay = 2000; // 2 seconds
    let tries = 0;
    const trySetBoxeverLoaded = () => {
      if (window._boxeverq && window.Boxever) {
        setboxeverLoaded(true);
      } else {
        tries++;
        if (tries < 3) {
          setTimeout(trySetBoxeverLoaded, delay);
        }
      }
    };
    const timer = setTimeout(trySetBoxeverLoaded, delay);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    setFormData(prevAddressData);
    if (boxeverLoaded && prevAddressData.firstName === '') {
      getSalesforceFlowData().then((res: SalesForceData): void => {
        if (res.profileData !== undefined) {
          setFormData(buildFormDataFromProfile(res));
        }
      });
    }
    if (prevAddressData.address2 !== '') {
      setShowAddress(true);
    }
  }, [boxeverLoaded, prevAddressData]);
  const [errors, setErrors] = useState<Partial<checkoutAddress>>({});
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setFormValid(checkValidForm(formData));
    validateForm(formData, changedFields, setErrors);
  }, [changedFields, formData]);
  const handleClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (checkValidForm(formData)) {
      setOrderAddress(formData);
      updateSteps(1);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const handleBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name } = e.target;
    if (!changedFields.includes(name)) {
      setChangedFields([...changedFields, name]);
    }
  };
  return (
    <>
      <ul className={classnames('mt-8')}>
        <li className={inputSpacingCss}>
          <label htmlFor="firstName">{dictionary.checkout_first_name}</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            onBlur={handleBlur}
            value={formData.firstName}
            onChange={handleChange}
            className={`${inputCss} ${errors.firstName && 'error'}`}
            placeholder={dictionary.checkout_first_name_placeholder}
          />
          {errors.firstName && (
            <ErrorWarning errorMessage={dictionary.checkout_first_name_validation} />
          )}
        </li>
        <li className={inputSpacingCss}>
          <label htmlFor="lastName">{dictionary.checkout_last_name}</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`${inputCss} ${errors.lastName && 'error'}`}
            placeholder={dictionary.checkout_last_name_placeholder}
          />
          {errors.lastName && (
            <ErrorWarning errorMessage={dictionary.checkout_last_name_validation} />
          )}
        </li>
        <li className={inputSpacingCss}>
          <label htmlFor="email">{dictionary.checkout_email}</label>
          <input
            type="text"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`${inputCss} ${errors.email && 'error'}`}
            placeholder={dictionary.checkout_email_placeholder}
          />
          {errors.email && <ErrorWarning errorMessage={dictionary.checkout_email_validation} />}
        </li>
        <li className={inputSpacingCss}>
          <label htmlFor="phone">{dictionary.checkout_phone}</label>
          <input
            type="text"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`${inputCss} ${errors.phone && 'error'}`}
            placeholder={dictionary.checkout_phone_placeholder}
          />
          {errors.phone && <ErrorWarning errorMessage={dictionary.checkout_phone_validation} />}
        </li>
        <li className={inputSpacingCss}>
          <label htmlFor="company">{dictionary.checkout_company}</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`${inputCss} ${errors.company && 'error'}`}
            placeholder={dictionary.checkout_company_placeholder}
          />
        </li>
        <li className={inputSpacingCss}>
          <label htmlFor="address">{dictionary.checkout_address}</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`${inputCss} ${errors.address && 'error'}`}
            placeholder={dictionary.checkout_address_placeholder}
          />
          {errors.address && <ErrorWarning errorMessage={dictionary.checkout_address_validation} />}

          <Button
            className={classnames('font-regular', 'text-blue', 'text-sm', 'pt-2')}
            onClick={() => setShowAddress(true)}
            text={dictionary.checkout_address_disclaimer}
            variant="link"
          />
        </li>
        {showAddress2 && (
          <li className={inputSpacingCss}>
            <input
              type="text"
              id="address2"
              name="address2"
              value={formData.address2}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={dictionary.checkout_address2_placeholder}
            />
          </li>
        )}
        <li className={inputSpacingCss}>
          <label htmlFor="city">{dictionary.checkout_city}</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`${inputCss} ${errors.city && 'error'}`}
            placeholder={dictionary.checkout_city_placeholder}
          />
          {errors.city && <ErrorWarning errorMessage={dictionary.checkout_city_validation} />}
        </li>
        <li className={classnames(inputSpacingCss, 'flex')}>
          <div className={classnames('w-2/3', 'pr-8')}>
            <label htmlFor="state">{dictionary.checkout_state}</label>
            <select
              onChange={handleChange}
              onBlur={handleBlur}
              id="state"
              name="state"
              value={formData.state}
              className={`${inputCss} ${errors.state && 'error'}`}
            >
              <option value="">{dictionary.checkout_state_placeholder}</option>
              {usStates.map((state) => (
                <option key={state.name} value={state.abbreviation}>
                  {state.name}
                </option>
              ))}
            </select>
            {errors.state && <ErrorWarning errorMessage={dictionary.checkout_state_validation} />}
          </div>
          <div className={classnames('w-1/3')}>
            <label htmlFor="zipCode">{dictionary.checkout_zip_code}</label>
            <input
              type="number"
              id="zipCode"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`${inputCss} ${errors.zipCode && 'error'}`}
              placeholder={dictionary.checkout_zip_code_placeholder}
              maxLength={5}
            />
            {errors.zipCode && (
              <ErrorWarning errorMessage={dictionary.checkout_zip_code_validation} />
            )}
          </div>
        </li>
      </ul>
      <Button
        className={
          formIsValid
            ? classnames('mt-4', 'w-full', 'text-center', 'md:w-auto', 'opacity-100')
            : classnames(
                'mt-4',
                'w-full',
                'text-center',
                'md:w-auto',
                'opacity-50',
                'cursor-not-allowed'
              )
        }
        linkField={continueToShippingCTA}
        onClick={handleClick}
      />
    </>
  );
};

const ShippingAddress = ({
  continueToShippingCTA,
  updateSteps,
  shippingAddressLabel,
  shippingMethodLabel,
  reviewLabel,
}: ShippingAddressProps): JSX.Element => {
  const dictionary = useDictionary();
  return (
    <>
      <div className={classnames('checkout-form', 'justify-center', 'w-full')}>
        <form className={classnames('pl-1')}>
          <fieldset>
            <legend>
              <Text
                field={shippingAddressLabel}
                tag="h2"
                className={classnames('text-body', 'text-solidBlack', 'font-medium', 'md:text-2xl')}
              />
            </legend>
            <div className={classnames('pt-2', 'italic')}>
              {dictionary.checkout_validation_message}
            </div>
            <ShippingAddressFields
              continueToShippingCTA={continueToShippingCTA}
              updateSteps={updateSteps}
            />
          </fieldset>
        </form>
        <div className={classnames('md:hidden')}>
          <div className={classnames('progress-title', 'inactive', 'pt-12')}>
            {shippingMethodLabel.value}
          </div>
          <div className={classnames('progress-title', 'inactive', 'pt-8', 'leading-1')}>
            {reviewLabel.value}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShippingAddress;
